import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { X, Mail, Bell, UserPlus, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Search, ArrowLeftRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import axios from 'axios'
import type { RadarBubble } from '@/services/api/teacher'
import { teacherApi } from '@/services/api/teacher'
import { apiClient, ApiErrorResponse } from '@/services/api/client'
import { adminApi, type AdminSubstitutionDetail, type TeacherInfo } from '@/services/api/admin'
import { useAnimationConfig } from './hooks/useReducedMotion'
import { GlassButton } from './ui/GlassButton'
import { useToast } from '@/components/ui/ToastProvider'
import { detectLocale, getTranslations, type Locale } from './i18n'
import { sortLessonsByDateTime } from '@/utils/lessonSort'
import { getStatusColor } from '@/utils/statusHelpers'
import { WaitingPoolMatrix } from '@/components/admin/WaitingPoolMatrix'
import { useAdminDashboardConfig } from '@/hooks/useAdminDashboardConfig'
import { onCascadeStateChanged } from '@/utils/cascadeEvents'
import { CancelBubbleModal } from './ui/CancelBubbleModal'
import { countCancelApiLiveLessons, isActiveAdminLesson } from './utils/activeAdminLessons'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'

interface SwitchViewConfig {
  label: string
  icon?: React.ReactNode
  onClick: () => void
}

interface AdminSlideOverProps {
  requestId: string
  bubble?: RadarBubble
  allBubbles?: RadarBubble[]
  onClose: () => void
  onRefresh: () => void
  onOpenMessageModal: () => void
  embedded?: boolean
  switchView?: SwitchViewConfig
}

const RESPONSE_LIMIT = 10

function formatHoursUntil(hours: number): string {
  if (hours < 1) {
    const mins = Math.round(hours * 60)
    return `in ${mins} Min.`
  }
  if (hours < 24) return `in ${Math.round(hours)} Std.`
  const days = Math.floor(hours / 24)
  const remainHours = Math.round(hours % 24)
  if (days === 1) return `in 1 Tag${remainHours > 0 ? ` ${remainHours} Std.` : ''}`
  return `in ${days} Tagen${remainHours > 0 ? ` ${remainHours} Std.` : ''}`
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

function SkeletonBlock({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded"
          style={{
            background: 'rgba(148, 163, 184, 0.2)',
            width: i === lines - 1 ? '60%' : '100%',
          }}
        />
      ))}
    </div>
  )
}

export function AdminSlideOver({
  requestId,
  bubble,
  allBubbles,
  onClose,
  onRefresh,
  onOpenMessageModal,
  embedded,
  switchView,
}: AdminSlideOverProps) {
  const animConfig = useAnimationConfig()
  const directorOn = useDirectorEnabled()
  const { showToast } = useToast()
  const locale: Locale = detectLocale()
  const t = getTranslations(locale).slideOver
  const { data: dashboardConfig } = useAdminDashboardConfig()

  const [detail, setDetail] = useState<AdminSubstitutionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const [showReminderInput, setShowReminderInput] = useState(false)
  const [reminderMessage, setReminderMessage] = useState('')
  const [sendingReminder, setSendingReminder] = useState(false)

  const [showAssignSearch, setShowAssignSearch] = useState(false)
  const [assignSearchQuery, setAssignSearchQuery] = useState('')
  const [assignSearchResults, setAssignSearchResults] = useState<TeacherInfo[]>([])
  const [assigning, setAssigning] = useState(false)
  const [assignSearching, setAssignSearching] = useState(false)

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const [pendingAssignTeacherId, setPendingAssignTeacherId] = useState<string | null>(null)
  const [assigningLessonId, setAssigningLessonId] = useState<string | null>(null)
  
  const [cancellingLessonId, setCancellingLessonId] = useState<string | null>(null)
  const [showCancelLessonConfirm, setShowCancelLessonConfirm] = useState(false)
  const [cancellingLesson, setCancellingLesson] = useState(false)

  const [showAllResponses, setShowAllResponses] = useState(false)

  const siblingBubbles = useMemo<RadarBubble[]>(() => {
    if (!bubble?.is_split || !bubble.id.startsWith('split_') || !allBubbles) return []
    const role = bubble.split_role
    const suffix = `_${role}`
    const lastIdx = bubble.id.lastIndexOf(suffix)
    const groupPrefix = lastIdx >= 0 ? bubble.id.substring(0, lastIdx) : bubble.id
    return allBubbles.filter(b =>
      b.id !== bubble.id &&
      b.id.startsWith(groupPrefix) &&
      b.is_split
    )
  }, [bubble, allBubbles])

  const abortControllerRef = useRef<AbortController | null>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const detailRef = useRef<AdminSubstitutionDetail | null>(null)
  detailRef.current = detail

  const fetchDetail = useCallback(async (reqId: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller

    setDetailError(null)
    if (!detailRef.current) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }
    try {
      const { data } = await apiClient.get<AdminSubstitutionDetail>(
        `/api/v1/admin/substitutions/${reqId}/detail`,
        { signal: controller.signal }
      )
      if (!controller.signal.aborted) {
        setDetail(data)
      }
    } catch (err: unknown) {
      if (axios.isCancel(err) || controller.signal.aborted) return
      if (!controller.signal.aborted) {
        setDetailError(err instanceof Error ? err.message : 'Fehler beim Laden')
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchDetail(requestId)
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [requestId, fetchDetail])

  useEffect(() => {
    setShowAllResponses(false)
    setShowReminderInput(false)
    setReminderMessage('')
    setShowAssignSearch(false)
    setAssignSearchQuery('')
    setAssignSearchResults([])
    setShowCancelConfirm(false)
    setAssigningLessonId(null)
    setCancellingLessonId(null)
    setShowCancelLessonConfirm(false)
  }, [requestId])

  useEffect(() => {
    if (embedded) return
    const interval = setInterval(() => {
      fetchDetail(requestId)
    }, 30000)
    return () => clearInterval(interval)
  }, [requestId, fetchDetail, embedded])

  useEffect(() => {
    const handleFocus = () => {
      fetchDetail(requestId)
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [requestId, fetchDetail])

  useEffect(() => {
    return onCascadeStateChanged((eventId) => {
      if (!requestId || (eventId && eventId !== requestId)) return
      fetchDetail(requestId)
    })
  }, [requestId, fetchDetail])

  useEffect(() => {
    if (embedded) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, embedded])

  useEffect(() => {
    if (!showAssignSearch) return
    if (!assignSearchQuery.trim()) {
      setAssignSearchResults([])
      return
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(async () => {
      setAssignSearching(true)
      try {
        const res = await adminApi.listTeachers({ search: assignSearchQuery, limit: dashboardConfig?.admin_search_limit_teachers ?? 10 })
        setAssignSearchResults(res.teachers || [])
      } catch {
        setAssignSearchResults([])
      } finally {
        setAssignSearching(false)
      }
    }, 300)
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [assignSearchQuery, showAssignSearch])

  const handleSendReminder = async () => {
    setSendingReminder(true)
    try {
      const lessonRequestId = detail?.lesson_request_id || bubble?.lesson_request_id
      if (!lessonRequestId) {
        showToast({ message: 'Keine Lektion gefunden', type: 'error', duration: 3000 })
        return
      }
      await teacherApi.sendRadarReminder([lessonRequestId], reminderMessage || undefined)
      showToast({ message: 'Erinnerung gesendet', type: 'success', duration: 3000 })
      setShowReminderInput(false)
      setReminderMessage('')
      onRefresh()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erinnerung fehlgeschlagen'
      showToast({ message: msg, type: 'error', duration: 5000 })
    } finally {
      setSendingReminder(false)
    }
  }

  const handleAssign = async (teacherId: string, lessonIds?: string[]) => {
    setAssigning(true)
    try {
      const res = await adminApi.assignSubstitution(requestId, teacherId, lessonIds)
      // noscore (Entscheidung 7): Override-Warnung sichtbar machen
      const warning = (res as { warning?: string | null } | undefined)?.warning
      if (warning) {
        showToast({ message: warning, type: 'warning', duration: 8000 })
      } else {
        showToast({ message: lessonIds ? 'Lektion zugewiesen' : 'Vertretung zugewiesen', type: 'success', duration: 3000 })
      }
      setShowAssignSearch(false)
      setAssignSearchQuery('')
      setAssignSearchResults([])
      setPendingAssignTeacherId(null)
      setAssigningLessonId(null)
      onRefresh()
      fetchDetail(requestId)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Zuweisung fehlgeschlagen'
      showToast({ message: msg, type: 'error', duration: 5000 })
    } finally {
      setAssigning(false)
    }
  }

  const handleAssignClick = (teacherId: string) => {
    if (assigningLessonId) {
      const lesson = detail?.lessons.find(l => l.id === assigningLessonId)
      if (lesson?.assigned_teacher_name) {
        setPendingAssignTeacherId(teacherId)
      } else {
        handleAssign(teacherId, [assigningLessonId])
      }
    } else {
      const alreadyAssigned = detail?.assigned_teacher_name || bubble?.assigned_teacher_name
      if (alreadyAssigned) {
        setPendingAssignTeacherId(teacherId)
      } else {
        handleAssign(teacherId)
      }
    }
  }

  const handleCancelLessonClick = (lessonId: string) => {
    setCancellingLessonId(lessonId)
    setShowCancelLessonConfirm(true)
  }

  const handleCancelLessonConfirm = async () => {
    if (!cancellingLessonId) return
    setCancellingLesson(true)
    try {
      await adminApi.cancelSubstitutionLessons([cancellingLessonId])
      showToast({ message: 'Lektion storniert', type: 'success', duration: 3000 })
      setShowCancelLessonConfirm(false)
      setCancellingLessonId(null)
      onRefresh()
      fetchDetail(requestId)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Stornierung fehlgeschlagen'
      showToast({ message: msg, type: 'error', duration: 5000 })
    } finally {
      setCancellingLesson(false)
    }
  }

  const handleCancelLessonCancel = () => {
    setShowCancelLessonConfirm(false)
    setCancellingLessonId(null)
  }

  const handleAssignLesson = (lessonId: string) => {
    setAssigningLessonId(lessonId)
    if (!showAssignSearch) {
      setShowAssignSearch(true)
    }
  }

  const getCancelErrorMessage = (err: unknown): { message: string; shouldClose: boolean } => {
    if (err instanceof ApiErrorResponse) {
      const status = err.statusCode
      const detail = err.detail.toLowerCase()
      
      if (status === 400) {
        if (detail.includes('cancelled')) {
          return { message: t.cancelErrors.alreadyCancelled, shouldClose: true }
        } else if (detail.includes('booked')) {
          return { message: t.cancelErrors.alreadyBooked, shouldClose: true }
        } else if (detail.includes('declined')) {
          return { message: t.cancelErrors.alreadyDeclined, shouldClose: true }
        }
        return { message: err.detail, shouldClose: false }
      } else if (status === 403) {
        return { message: t.cancelErrors.noPermission, shouldClose: false }
      } else if (status === 404) {
        return { message: t.cancelErrors.notFound, shouldClose: true }
      }
      return { message: err.detail || t.cancelErrors.generic, shouldClose: false }
    } else if (err instanceof Error) {
      return { message: err.message, shouldClose: false }
    }
    return { message: t.cancelErrors.generic, shouldClose: false }
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      await adminApi.cancelSubstitution(requestId)
      showToast({ message: 'Anfrage storniert', type: 'success', duration: 3000 })
      setShowCancelConfirm(false)
      onRefresh()
      onClose()
    } catch (err: unknown) {
      const { message, shouldClose } = getCancelErrorMessage(err)
      showToast({ message, type: 'error', duration: 5000 })
      
      if (shouldClose) {
        onRefresh()
        onClose()
      }
    } finally {
      setCancelling(false)
    }
  }

  const handleCancelClick = () => {
    if (siblingBubbles.length > 0) {
      setShowCancelModal(true)
    } else {
      setShowCancelConfirm(true)
    }
  }

  const handleCancelModalConfirm = async (selectedLessonRequestIds: string[]) => {
    setCancelling(true)
    try {
      const allLessonIds = [
        ...(bubble?.lesson_request_ids || []),
        ...siblingBubbles.flatMap(b => b.lesson_request_ids || []),
      ]

      if (selectedLessonRequestIds.length >= allLessonIds.length) {
        await adminApi.cancelSubstitution(requestId)
      } else {
        await adminApi.cancelSubstitutionLessons(selectedLessonRequestIds)
      }
      showToast({ message: 'Storniert', type: 'success', duration: 3000 })
      setShowCancelModal(false)
      onRefresh()
      onClose()
    } catch (err: unknown) {
      const { message, shouldClose } = getCancelErrorMessage(err)
      showToast({ message, type: 'error', duration: 5000 })
      if (shouldClose) {
        setShowCancelModal(false)
        onRefresh()
        onClose()
      }
    } finally {
      setCancelling(false)
    }
  }

  const handleRefreshDetail = () => {
    fetchDetail(requestId)
    onRefresh()
  }

  const activeLessons = useMemo(
    () => (detail?.lessons ?? []).filter(l => isActiveAdminLesson(l.status)),
    [detail],
  )
  const activeLessonCount = activeLessons.length
  const headerLesson = activeLessons[0] ?? detail?.lessons?.[0]

  const hoursUntil = detail?.hours_until_lesson ?? bubble?.hours_until_lesson ?? 9999
  const isPanicZone = detail?.panic_zone_activated || bubble?.is_in_panic_zone || false
  // Zone thresholds (24h middle, 4h outer) — approximation; exact calculation from radarConfig on backend
  const isMiddleZone = !isPanicZone && hoursUntil <= 24 && hoursUntil > 4

  const zone = isPanicZone
    ? { label: t.panicZone, color: 'text-red-600 bg-red-50' }
    : isMiddleZone
    ? { label: t.middleZone, color: 'text-orange-600 bg-orange-50' }
    : { label: t.outerZone, color: 'text-gray-600 bg-gray-50' }

  const currentStatus = detail?.status || bubble?.status || 'pending'
  const statusLabel = (!directorOn && (currentStatus === 'director_pending' || currentStatus === 'director_returned'))
    ? ((t.statusLabels as Record<string, string>).pending || 'Offen')
    : ((t.statusLabels as Record<string, string>)[currentStatus] || currentStatus)
  // frost-v2 (E15/B5): Gate-Banner — detail-Status hat Vorrang vor dem
  // möglicherweise veralteten bubble.gate_pending (Race nach Freigabe).
  // LiveStatus/Listen bauen synthetische Bubbles ohne gate_pending — dort
  // ist bubble.status der Request-Status (director_pending). Incoming nie.
  const gateActive = directorOn && (detail
    ? detail.status === 'director_pending'
    : !!(
        bubble &&
        bubble.bubble_type !== 'incoming' &&
        (bubble.gate_pending || bubble.status === 'director_pending')
      ))
  const statusColor =
    currentStatus === 'accepted' || currentStatus === 'filled' || currentStatus === 'booked'
      ? 'bg-green-100 text-green-700'
      : currentStatus === 'declined' || currentStatus === 'cancelled'
      ? 'bg-red-100 text-red-700'
      : currentStatus === 'partially_filled'
      ? 'bg-orange-100 text-orange-700'
      : directorOn && (currentStatus === 'director_pending' || gateActive)
      ? 'bg-sky-100 text-sky-800'
      : 'bg-orange-100 text-orange-700'

  const teachersPending = detail
    ? detail.responses.filter(r => r.response_type === 'pending').length
    : bubble?.teachers_pending ?? 0
  const isAccepted = currentStatus === 'accepted' || currentStatus === 'filled' || currentStatus === 'booked'

  const visibleResponses = detail
    ? showAllResponses
      ? detail.responses
      : detail.responses.slice(0, RESPONSE_LIMIT)
    : []
  const hiddenResponseCount = detail ? Math.max(0, detail.responses.length - RESPONSE_LIMIT) : 0

  return (
    <>
    <AnimatePresence>
      <motion.div
        key="main-panel"
        ref={panelRef}
        className={embedded
          ? "h-full flex flex-col overflow-hidden bg-white border-l border-gray-200"
          : "fixed right-0 bottom-0 flex flex-col md:w-[560px] w-full"
        }
        style={embedded ? undefined : {
          top: '3.5rem',
          zIndex: 40,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderLeft: '1px solid rgba(226, 232, 240, 0.6)',
          boxShadow: '-8px 0 40px rgba(0, 0, 0, 0.1)',
        }}
        initial={embedded ? false : { x: '100%' }}
        animate={embedded ? undefined : { x: 0 }}
        exit={embedded ? undefined : { x: '100%' }}
        transition={embedded ? undefined : (
          animConfig.reducedMotion
            ? { type: 'tween', duration: 0 }
            : { type: 'spring', stiffness: 400, damping: 30 }
        )}
        role="dialog"
        aria-modal="false"
        aria-label={t.title}
      >
        <div role="status" aria-live="polite" className="sr-only" />

        {/* Header */}
        <div
          className="flex-shrink-0 px-5 py-4 border-b"
          style={{
            background: 'linear-gradient(180deg, rgba(248,250,252,0.95), rgba(255,255,255,0.95))',
            borderColor: 'rgba(226, 232, 240, 0.5)',
          }}
        >
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] font-semibold text-gray-900">{t.title}</h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {activeLessonCount > 1 ? (
                  <>{activeLessonCount} {t.lessons.toLowerCase()}</>
                ) : (
                  <>
                    {headerLesson?.date
                      ? new Date(headerLesson.date).toLocaleDateString(locale === 'de' ? 'de-CH' : 'en-US')
                      : bubble?.lesson_date
                      ? new Date(bubble.lesson_date).toLocaleDateString(locale === 'de' ? 'de-CH' : 'en-US')
                      : '—'}
                    {(headerLesson?.time_slot_name || bubble?.time_slot_name) && ` · ${headerLesson?.time_slot_name || bubble?.time_slot_name}`}
                    {(headerLesson?.subject_name || bubble?.subject_name) && ` · ${headerLesson?.subject_name || bubble?.subject_name}`}
                    {(headerLesson?.klass_name || bubble?.klass_name) && ` ${headerLesson?.klass_name || bubble?.klass_name}`}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={handleRefreshDetail}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${loading || refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* frost-v2 (E15): Gate-Hinweis-Banner — Verteilung pausiert, solange
            die Anfrage beim Direktor auf Freigabe wartet. */}
        {gateActive && (
          <div
            className="flex-shrink-0 px-5 py-2.5 border-b flex items-center gap-2"
            style={{
              background: 'rgba(224, 242, 254, 0.4)',
              borderColor: 'rgba(191, 219, 254, 0.8)',
            }}
            data-testid="gate-banner"
          >
            <span className="text-xs font-medium text-sky-800">{getTranslations(locale).admin.gateBanner}</span>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Content */}
          <div>
          {/* Unterricht */}
          <div className="px-5 py-4 border-b border-gray-200/30">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
              {activeLessonCount > 1
                ? `${t.lessons} (${activeLessonCount})`
                : t.lesson}
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label={`Details für ${activeLessonCount || 1} Lektionen`}>
                <thead className="text-[10px] text-gray-500 uppercase border-b">
                  <tr>
                    <th scope="col" className="pb-1.5 text-left font-medium">Datum</th>
                    <th scope="col" className="pb-1.5 text-left font-medium">Zeit</th>
                    <th scope="col" className="pb-1.5 text-left font-medium">Fach</th>
                    <th scope="col" className="pb-1.5 text-left font-medium">Klasse</th>
                    <th scope="col" className="pb-1.5 text-left font-medium">Raum</th>
                    <th scope="col" className="pb-1.5 text-left font-medium">Vertretung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortLessonsByDateTime(detail?.lessons || []).map((lesson) => {
                    const isCancelled = lesson.status === 'cancelled'
                    const isInactive = !isActiveAdminLesson(lesson.status)
                    return (
                    <tr key={lesson.id} className={isInactive ? 'opacity-40' : ''}>
                      <td className={`py-1.5 text-gray-700 ${isInactive ? 'line-through' : ''}`}>
                        {new Date(lesson.date).toLocaleDateString(locale === 'de' ? 'de-CH' : 'en-US')}
                      </td>
                      <td className={`py-1.5 text-gray-600 ${isInactive ? 'line-through' : ''}`}>
                        {lesson.time_slot_name || '—'}
                      </td>
                      <td className={`py-1.5 font-medium text-gray-900 ${isInactive ? 'line-through' : ''}`}>
                        {lesson.subject_name || '—'}
                      </td>
                      <td className={`py-1.5 text-gray-700 ${isInactive ? 'line-through' : ''}`}>
                        {lesson.klass_name || '—'}
                      </td>
                      <td className={`py-1.5 text-gray-600 text-xs ${isInactive ? 'line-through' : ''}`}>
                        {lesson.room_name || '—'}
                      </td>
                      <td className="py-1.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {lesson.status && (
                            <span className={`px-1.5 py-0.5 text-[9px] rounded-full font-medium ${getStatusColor(lesson.status)}`}>
                              {(t.lessonStatusLabels as Record<string, string>)[lesson.status] || lesson.status}
                            </span>
                          )}
                          {isCancelled ? (
                            <span className="text-xs text-gray-400 italic">Storniert</span>
                          ) : isInactive ? null : lesson.assigned_teacher_name ? (
                            <span className="text-xs text-green-700 font-medium">
                              {lesson.assigned_teacher_short_code}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">—</span>
                          )}
                          {!isInactive && (
                            <>
                              <button
                                onClick={() => handleAssignLesson(lesson.id)}
                                className="text-[10px] text-blue-600 hover:text-blue-700 underline"
                              >
                                Ändern
                              </button>
                              <button
                                onClick={() => handleCancelLessonClick(lesson.id)}
                                className="text-[10px] text-red-600 hover:text-red-700 underline"
                              >
                                Stornieren
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {detail?.reason && (
              <div className="mt-3">
                <span className="text-gray-400 text-xs">{t.reason}</span>
                <div className="text-sm text-gray-700 mt-0.5">{detail.reason}</div>
              </div>
            )}
            {detail?.notes && (
              <div className="mt-2">
                <span className="text-gray-400 text-xs">Notizen</span>
                <div className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{detail.notes}</div>
              </div>
            )}
            {detail?.created_at && (
              <div className="mt-2">
                <span className="text-gray-400 text-xs">Erstellt</span>
                <div className="text-xs text-gray-500 mt-0.5">{formatRelativeTime(detail.created_at)}</div>
              </div>
            )}
            <div className="mt-3 flex gap-1.5 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${zone.color}`}>
                {zone.label} – {formatHoursUntil(hoursUntil)}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                {statusLabel}
              </span>
              {(detail?.needs_admin_assignment || bubble?.needs_admin_assignment) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  manuelle Zuweisung nötig
                </span>
              )}
              {(detail?.prep?.status || bubble?.prep_status_summary) && (detail?.prep?.status || bubble?.prep_status_summary) !== 'no_prep' && (detail?.prep?.status || bubble?.prep_status_summary) !== 'none' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Prep
                </span>
              )}
              {detail?.is_multi_day && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {t.multiDay}
                </span>
              )}
            </div>
          </div>

          {/* Beteiligte */}
          <div className="px-5 py-4 border-b border-gray-200/30">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">{t.participants}</h3>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">{t.requesting}</div>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                  >
                    {getInitials(detail?.requesting_teacher_name || bubble?.requester_name || '')}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{detail?.requesting_teacher_name || bubble?.requester_name}</div>
                    <div className="text-xs text-gray-500">
                      {detail?.requesting_teacher_short_code || bubble?.requester_short_code}
                      {(detail?.requesting_teacher_faculty || bubble?.requester_faculty) && ` · ${detail?.requesting_teacher_faculty || bubble?.requester_faculty}`}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">{t.substitute}</div>
                {detail?.assigned_teachers && detail.assigned_teachers.length > 0 ? (
                  <div className="space-y-2">
                    {detail.assigned_teachers.map((teacher) => (
                      <div key={teacher.teacher_id} className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                        >
                          {getInitials(teacher.teacher_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate text-green-700">
                            {teacher.teacher_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {teacher.teacher_short_code} · {teacher.lesson_count} {teacher.lesson_count === 1 ? 'Lektion' : 'Lektionen'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : detail?.assigned_teacher_name || bubble?.assigned_teacher_name ? (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                    >
                      {getInitials(detail?.assigned_teacher_name || bubble?.assigned_teacher_name || '')}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate text-green-700">
                        {detail?.assigned_teacher_name || bubble?.assigned_teacher_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {detail?.assigned_teacher_short_code || bubble?.assigned_teacher_short_code}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 italic">{t.notAssigned}</div>
                )}
              </div>
            </div>
          </div>

          {/* Antworten */}
          <div className="px-5 py-4 border-b border-gray-200/30">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
              {t.responses}
              {detail && ` (${detail.responses.length})`}
            </h3>
            {loading && !detail ? (
              <SkeletonBlock lines={4} />
            ) : detailError ? (
              <div className="text-sm text-red-600">{detailError}</div>
            ) : detail && detail.responses.length === 0 ? (
              <div className="text-sm text-gray-400 italic">{t.noResponses}</div>
            ) : (
              <div className="relative pl-5">
                <div
                  className="absolute left-[6px] top-1 bottom-1 w-[2px] rounded-full"
                  style={{ background: 'rgba(226, 232, 240, 0.6)' }}
                />
                <div className="space-y-3">
                  {visibleResponses.map((r, i) => {
                    const dotColor =
                      r.response_type === 'accepted' ? '#22c55e'
                      : r.response_type === 'declined' ? '#ef4444'
                      : r.response_type === 'pending' ? '#f97316'
                      : '#9ca3af'
                    const bgDot =
                      r.response_type === 'accepted' ? '#f0fdf4'
                      : r.response_type === 'declined' ? '#fef2f2'
                      : r.response_type === 'pending' ? '#fff7ed'
                      : '#f9fafb'
                    const detailText =
                      r.response_type === 'accepted' ? `Zugesagt${r.responded_at ? ` · ${formatRelativeTime(r.responded_at)}` : ''}`
                      : r.response_type === 'declined' ? (r.decline_reason ? `"${r.decline_reason}"` : `Abgelehnt${r.responded_at ? ` · ${formatRelativeTime(r.responded_at)}` : ''}`)
                      : r.response_type === 'pending' ? (r.reminder_count > 0 ? `Offen · erinnert ${formatRelativeTime(r.responded_at || '')}` : 'Offen')
                      : 'Ignoriert'

                    return (
                      <div key={`${r.teacher_id}-${i}`} className="relative">
                        <div
                          className="absolute -left-5 top-[5px] w-2.5 h-2.5 rounded-full border-2"
                          style={{ borderColor: dotColor, background: bgDot }}
                        />
                        <div className="text-[13px] font-medium text-gray-900">
                          {r.teacher_name} <span className="text-gray-400 font-normal">({r.teacher_short_code})</span>
                        </div>
                        <div className="text-[11px] text-gray-500">{detailText}</div>
                      </div>
                    )
                  })}
                </div>
                {hiddenResponseCount > 0 && !showAllResponses && (
                  <button
                    onClick={() => setShowAllResponses(true)}
                    className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ChevronDown className="w-3 h-3" />
                    {hiddenResponseCount} {t.moreResponses}
                  </button>
                )}
                {showAllResponses && hiddenResponseCount > 0 && (
                  <button
                    onClick={() => setShowAllResponses(false)}
                    className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ChevronUp className="w-3 h-3" />
                    {t.lessResponses}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Waiting Pool Assembly (Admin) — immer laden; Matrix blendet leer aus */}
          {detail && (
            <WaitingPoolMatrix requestId={requestId} hideWhenEmpty />
          )}

          {/* Prep */}
          <div className="px-5 py-4 border-b border-gray-200/30">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">{t.prep}</h3>
            {loading && !detail ? (
              <SkeletonBlock lines={2} />
            ) : detail ? (
              <div>
                {detail.prep.status === 'complete' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    {t.prepComplete}
                  </span>
                )}
                {detail.prep.status === 'partial' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    {t.prepPartial}
                  </span>
                )}
                {detail.prep.status === 'prep_requested' && (
                  <div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      {t.prepRequested}
                    </span>
                    {detail.prep.requested_from_name && (
                      <div className="text-xs text-gray-500 mt-1">
                        {t.requestedFrom}: {detail.prep.requested_from_name}
                        {detail.prep.requested_at && ` · ${formatRelativeTime(detail.prep.requested_at)}`}
                      </div>
                    )}
                  </div>
                )}
                {detail.prep.status === 'none' && (
                  <span className="text-sm text-gray-400 italic">{t.prepNoInfo}</span>
                )}
              </div>
            ) : null}
          </div>
        </div>
        </div>

        {/* Actions Footer */}
        <div
          className="flex-shrink-0 border-t px-5 py-4 space-y-2"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(24px)',
            borderColor: 'rgba(226, 232, 240, 0.5)',
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <GlassButton
              onClick={onOpenMessageModal}
              disabled={!bubble}
              title={!bubble ? 'Erst verfügbar wenn Radar geladen' : undefined}
              variant="secondary"
              size="sm"
              className="!text-xs"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {t.sendMessage}
            </GlassButton>
            <GlassButton
              onClick={() => {
                if (!showReminderInput) {
                  setShowReminderInput(true)
                } else {
                  handleSendReminder()
                }
              }}
              variant="secondary"
              size="sm"
              disabled={sendingReminder || teachersPending === 0 || isAccepted}
              className="!text-xs"
            >
              <Bell className="w-3.5 h-3.5 mr-1.5" />
              {sendingReminder ? t.sending : t.sendReminder}
              {!showReminderInput && teachersPending > 0 && ` (${teachersPending})`}
            </GlassButton>
          </div>

          {showReminderInput && (
            <div className="space-y-2">
              <textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder={t.reminderPlaceholder}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                maxLength={500}
              />
              <div className="flex gap-2">
                <GlassButton
                  onClick={handleSendReminder}
                  disabled={sendingReminder}
                  variant="primary"
                  size="sm"
                  className="flex-1 !text-xs"
                >
                  {sendingReminder ? t.sending : t.sendReminder}
                </GlassButton>
                <GlassButton
                  onClick={() => {
                    setShowReminderInput(false)
                    setReminderMessage('')
                  }}
                  variant="secondary"
                  size="sm"
                  className="!text-xs"
                >
                  {t.cancel}
                </GlassButton>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <GlassButton
              onClick={() => {
                setShowAssignSearch(!showAssignSearch)
                setAssigningLessonId(null)
              }}
              variant="secondary"
              size="sm"
              className="!text-xs"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1.5" />
              {t.assign}
            </GlassButton>
            {switchView && (
              <GlassButton
                onClick={switchView.onClick}
                variant="secondary"
                size="sm"
                className="!text-xs"
              >
                {switchView.icon ?? <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" />}
                {switchView.label}
              </GlassButton>
            )}
          </div>

          {showAssignSearch && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={assignSearchQuery}
                  onChange={(e) => setAssignSearchQuery(e.target.value)}
                  placeholder={t.searchTeacher}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              {assignSearching ? (
                <div className="text-xs text-gray-400 text-center py-2">...</div>
              ) : assignSearchResults.length === 0 && assignSearchQuery.trim() ? (
                <div className="text-xs text-gray-400 text-center py-2">{t.noTeachersFound}</div>
              ) : (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {assignSearchResults.map((teacher) => (
                    <button
                      key={teacher.id}
                      onClick={() => handleAssignClick(teacher.id)}
                      disabled={assigning}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 transition text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                      >
                        {getInitials(`${teacher.first_name} ${teacher.last_name}`)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{teacher.first_name} {teacher.last_name}</div>
                        <div className="text-[10px] text-gray-500">{teacher.teacher_code || '—'}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Assign Confirmation */}
          <AnimatePresence>
            {pendingAssignTeacherId && (
              <motion.div
                key="assign-confirm"
                className="rounded-xl p-3 border"
                style={{
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <p className="text-xs text-blue-700 mb-2">
                  {assigningLessonId ? (
                    <>
                      Lektion ist bereits zugewiesen an{' '}
                      {detail?.lessons.find(l => l.id === assigningLessonId)?.assigned_teacher_name}.
                      Überschreiben?
                    </>
                  ) : (
                    <>
                      Bereits zugewiesen an:{' '}
                      {detail?.assigned_teachers?.map(t => t.teacher_short_code).join(', ') ||
                       detail?.assigned_teacher_name ||
                       bubble?.assigned_teacher_name}.
                      Alle überschreiben?
                    </>
                  )}
                </p>
                <div className="flex gap-2">
                  <GlassButton
                    onClick={() => {
                      if (assigningLessonId) {
                        handleAssign(pendingAssignTeacherId, [assigningLessonId])
                      } else {
                        handleAssign(pendingAssignTeacherId)
                      }
                    }}
                    disabled={assigning}
                    variant="primary"
                    size="sm"
                    className="flex-1 !text-xs"
                  >
                    {assigning ? t.assigning : 'Überschreiben'}
                  </GlassButton>
                  <GlassButton
                    onClick={() => {
                      setPendingAssignTeacherId(null)
                      setAssigningLessonId(null)
                    }}
                    variant="secondary"
                    size="sm"
                    className="!text-xs"
                  >
                    {t.cancel}
                  </GlassButton>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <GlassButton
            onClick={handleCancelClick}
            variant="danger"
            size="sm"
            className="w-full !text-xs"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            {t.cancelRequest}
          </GlassButton>

          {/* Cancel Confirm */}
          <AnimatePresence>
            {showCancelConfirm && (
              <motion.div
                key="cancel-confirm"
                className="rounded-xl p-3 border"
                style={{
                  background: 'rgba(239, 68, 68, 0.05)',
                  borderColor: 'rgba(239, 68, 68, 0.3)',
                  zIndex: 60,
                }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <p className="text-xs text-red-700 mb-2">{t.cancelConfirm}</p>
                <div className="flex gap-2">
                  <GlassButton
                    onClick={handleCancel}
                    disabled={cancelling}
                    variant="danger"
                    size="sm"
                    className="flex-1 !text-xs"
                  >
                    {cancelling ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        {t.cancelling}
                      </>
                    ) : t.confirm}
                  </GlassButton>
                  <GlassButton
                    onClick={() => setShowCancelConfirm(false)}
                    variant="secondary"
                    size="sm"
                    className="!text-xs"
                  >
                    {t.cancel}
                  </GlassButton>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      {/* Cancel Lesson Confirm */}
      <AnimatePresence key="cancel-lesson-modal">
        {showCancelLessonConfirm && cancellingLessonId && (
          <motion.div
            key="cancel-lesson-confirm"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(8px)',
              }}
              onClick={handleCancelLessonCancel}
            />
            <motion.div
              className="relative w-full max-w-md rounded-2xl border"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(24px)',
                borderColor: 'rgba(255, 255, 255, 0.6)',
                boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Lektion stornieren?
                </h3>
                {(() => {
                  const lesson = detail?.lessons.find(l => l.id === cancellingLessonId)
                  if (!lesson) return null
                  const isLastLesson = countCancelApiLiveLessons(detail?.lessons) === 1
                  return (
                    <div className="space-y-3">
                      <div className="text-sm text-gray-700">
                        <div className="font-medium">
                          {new Date(lesson.date).toLocaleDateString('de-CH')} {lesson.time_slot_name}
                        </div>
                        <div className="text-gray-500">
                          {lesson.subject_name} {lesson.klass_name}
                        </div>
                      </div>
                      {isLastLesson && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-xs text-yellow-800">
                            <strong>Warnung:</strong> Dies ist die letzte Lektion. Der gesamte Request wird storniert.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })()}
                <div className="flex gap-2 mt-6">
                  <GlassButton
                    onClick={handleCancelLessonConfirm}
                    disabled={cancellingLesson}
                    variant="danger"
                    size="sm"
                    className="flex-1"
                  >
                    {cancellingLesson ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                        Storniere...
                      </>
                    ) : (
                      'Stornieren'
                    )}
                  </GlassButton>
                  <GlassButton
                    onClick={handleCancelLessonCancel}
                    disabled={cancellingLesson}
                    variant="secondary"
                    size="sm"
                  >
                    Abbrechen
                  </GlassButton>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
      {bubble && (
        <CancelBubbleModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelModalConfirm}
          currentBubble={bubble}
          siblingBubbles={siblingBubbles}
          cancelling={cancelling}
        />
      )}
    </>
  )
}

function formatRelativeTime(isoString: string): string {
  if (!isoString) return ''
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'gerade eben'
  if (diffMin < 60) return `vor ${diffMin} Min.`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `vor ${diffHours} Std.`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return 'gestern'
  return `vor ${diffDays} Tagen`
}
